// The device view — Live's strip along the bottom of the window, showing
// the selected track's chain left to right. Each device is a box with its
// power switch, its name and its controls: EQ Eight, Delay and Reverb get
// views of their own (the curve, the echoes, the tail), every other device
// its parameters as knobs. A control moving is heard at once through the
// browser's stand-in (lib/device-graph.ts) and proposed to the Set on
// release, so a knob turned here turns Live's.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeviceState } from "@shared/sync/state.js";
import { HEARD_DEVICES } from "@web-shared/lib/device-graph";
import { isPlaceholder } from "@shared/sync/devices.js";
import { deviceOn, findParam } from "@web-shared/lib/device-params";
import { deviceCatalog } from "@web-shared/lib/live-devices";
import { stockDevice } from "@web-shared/lib/live-device-defaults";
import { cn } from "@web-shared/lib/utils";
import { CurveDevice } from "@web-shared/components/devices/CurveDevice";
import { DelayEditor } from "@web-shared/components/devices/DelayEditor";
import { EqEightEditor } from "@web-shared/components/devices/EqEightEditor";
import { GenericDevice } from "@web-shared/components/devices/GenericDevice";
import { ReverbEditor } from "@web-shared/components/devices/ReverbEditor";
import type { EditorProps } from "@web-shared/components/devices/types";

export interface DeviceLaneProps {
  /** The track's name, and its colour for the strip's edge. */
  title: string;
  color?: string;
  kind: "audio" | "midi";
  /** The chain as the room holds it; undefined ⇒ the Set hasn't reported it. */
  devices: DeviceState[] | undefined;
  bpm: number;
  /** Absent ⇒ read-only (listening in). */
  onParam?: (device: number, param: number, value: number) => void;
  /** The chain with the controls' drafts applied while one moves, null once
   * nothing is moving — so the lane is heard as the knob turns. */
  onPreview?: (devices: DeviceState[] | null) => void;
  onInsert?: (index: number, name: string) => void;
  onRemove?: (index: number) => void;
  busy?: boolean;
  /** Hear the chain approximately, or dry. */
  hear: boolean;
  onHear?: (on: boolean) => void;
  onClose: () => void;
  /** A phone: the chain as chips, one device shown at a time. */
  compact?: boolean;
}

/** The editor a device gets. */
export function editorFor(name: string): (props: EditorProps) => React.JSX.Element {
  switch (name) {
    case "EQ Eight":
      return EqEightEditor;
    case "Delay":
      return DelayEditor;
    case "Reverb":
      return ReverbEditor;
    case "Auto Filter":
    case "EQ Three":
    case "Channel EQ":
    case "Compressor":
    case "Glue Compressor":
    case "Limiter":
      return CurveDevice;
    default:
      return GenericDevice;
  }
}

const key = (d: number, p: number) => `${d}:${p}`;

export function DeviceLane({ title, color, kind, devices, bpm, onParam, onPreview, onInsert, onRemove, busy = false, hear, onHear, onClose, compact = false }: DeviceLaneProps) {
  const [picking, setPicking] = useState(false);
  // Folded devices (Live's: a device folds to its title, turned on its
  // side), by place and name so a reshape doesn't fold the wrong one.
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const foldKey = (di: number, name: string) => `${di}:${name}`;
  const toggleFold = (k: string) =>
    setFolded((f) => {
      const next = new Set(f);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const count = devices?.length ?? 0;
  const lastCount = useRef(count);
  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // A new device lands at the end of the chain: bring it into view.
    if (count > lastCount.current) requestAnimationFrame(() => scroller.current?.scrollTo({ left: scroller.current.scrollWidth, behavior: "smooth" }));
    lastCount.current = count;
  }, [count]);
  useEffect(() => setFolded(new Set()), [title]);
  useEffect(() => setPicking(false), [title]);
  const canAdd = devices !== undefined && !!onInsert && !busy;
  const [drafts, setDrafts] = useState<ReadonlyMap<string, number>>(new Map());
  // A different chain (another track, a reshape) drops what was moving.
  const shape = devices?.map((d) => `${d.name}/${d.params.length}`).join("|") ?? "";
  useEffect(() => setDrafts(new Map()), [shape, title]);

  const shown = useMemo(() => {
    if (!devices || drafts.size === 0) return devices;
    return devices.map((d, di) => ({ ...d, params: d.params.map((p, pi) => (drafts.has(key(di, pi)) ? { ...p, value: drafts.get(key(di, pi))! } : p)) }));
  }, [devices, drafts]);

  useEffect(() => {
    onPreview?.(drafts.size > 0 && shown ? shown : null);
  }, [shown, drafts.size, onPreview]);
  useEffect(() => () => onPreview?.(null), [onPreview]);

  const editable = !!onParam && !busy;
  const set = useCallback(
    (device: number, param: number, raw: number, commit: boolean) => {
      if (!onParam) return;
      if (commit) {
        onParam(device, param, raw);
        setDrafts((m) => {
          if (!m.has(key(device, param))) return m;
          const next = new Map(m);
          next.delete(key(device, param));
          return next;
        });
      } else {
        setDrafts((m) => new Map(m).set(key(device, param), raw));
      }
    },
    [onParam],
  );

  return (
    <section className="flex shrink-0 flex-col border-t border-panelline bg-sunk" data-testid="device-lane" aria-label={`${title} devices`}>
      <header className="flex h-10 items-center gap-2 border-b border-panelline px-3 text-xs sm:h-8">
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color ?? "var(--primary)" }} />
        <span className="min-w-0 truncate font-semibold">{title}</span>
        <span className="hidden text-[10px] text-muted-foreground sm:inline">
          {devices === undefined ? "chain not reported yet — the Set's extension sends it" : devices.length === 0 ? "no devices" : `${devices.length} device${devices.length === 1 ? "" : "s"}`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {devices && devices.length > 1 && !picking && (
            <button
              type="button"
              onClick={() => setFolded(folded.size >= devices.length ? new Set() : new Set(devices.map((d, di) => foldKey(di, d.name))))}
              className="flex h-7 items-center rounded-full bg-white/5 px-3 text-[11px] font-semibold text-muted-foreground hover:text-foreground sm:h-6"
              title="Fold every device to its title, or unfold them all"
            >
              {folded.size >= devices.length ? "Unfold all" : "Fold all"}
            </button>
          )}
          {canAdd && (
            <button
              type="button"
              onClick={() => setPicking(!picking)}
              aria-expanded={picking}
              className={cn(
                "flex h-7 items-center gap-1 rounded-full px-3 text-[11px] font-semibold sm:h-6",
                picking ? "bg-white/10 text-foreground" : "bg-primary text-primary-foreground",
              )}
            >
              {picking ? "Done" : "+ Add"}
            </button>
          )}
          {onHear && (
            <label className="flex items-center gap-1 text-[10px] text-muted-foreground" title="Hear the chain through the browser's stand-ins for Live's devices (EQ Eight, Delay, Reverb, filters, dynamics, saturation, modulation, Utility) — close to Live, not Live's sound">
              <input type="checkbox" checked={hear} onChange={(e) => onHear(e.target.checked)} aria-label="Hear devices" />
              <span className={cn(compact && "hidden")}>hear devices</span>
              {compact && <span>hear</span>}
            </label>
          )}
          <button type="button" onClick={onClose} className="flex size-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground sm:size-6" aria-label="Close device view" title="Close the device view">
            ✕
          </button>
        </div>
      </header>
      {picking && canAdd ? (
        <DevicePicker
          kind={kind}
          onPick={(name) => {
            onInsert?.(devices!.length, name);
            setPicking(false);
          }}
        />
      ) : (
        <div
          ref={scroller}
          className={cn(
            "scrollbar-thin flex min-h-[212px] items-stretch gap-2 overflow-x-auto p-2",
            // A phone swipes device to device, each landing at the edge.
            compact && "snap-x snap-mandatory scroll-px-2 overscroll-x-contain",
          )}
        >
          {shown?.map((d, di) => (
            <DeviceBox
              key={`${di}:${d.name}`}
              device={d}
              bpm={bpm}
              editable={editable}
              compact={compact}
              folded={folded.has(foldKey(di, d.name))}
              onFold={() => toggleFold(foldKey(di, d.name))}
              onToggle={() => {
                const on = findParam(d, "Device On");
                if (on) set(di, on.index, deviceOn(d) ? on.p.min : on.p.max, true);
              }}
              onRemove={onRemove && !busy ? () => onRemove(di) : undefined}
              set={(param, raw, commit) => set(di, param, raw, commit)}
            />
          ))}
          {canAdd && <AddDevice onOpen={() => setPicking(true)} empty={shown!.length === 0} compact={compact} />}
          {shown === undefined && (
            <div className="flex items-center px-4 text-xs text-muted-foreground">The Set hasn&rsquo;t reported this track&rsquo;s devices yet. They appear once its extension polls.</div>
          )}
        </div>
      )}
    </section>
  );
}

/** A vertical swipe on a device's title: past this many px, it folds or unfolds. */
const FOLD_SWIPE_PX = 28;

function DeviceBox({
  device,
  bpm,
  editable,
  compact,
  folded,
  onFold,
  onToggle,
  onRemove,
  set,
}: {
  device: DeviceState;
  bpm: number;
  editable: boolean;
  compact: boolean;
  folded: boolean;
  onFold: () => void;
  onToggle: () => void;
  onRemove?: () => void;
  set: EditorProps["set"];
}) {
  // Live folds a device with a double-click on its title; a finger swipes
  // the title up or down. Sideways stays the row's scroll.
  const swipe = useRef<{ x: number; y: number; done: boolean } | null>(null);
  const swipeHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest("[data-noswipe]")) return;
      swipe.current = { x: e.clientX, y: e.clientY, done: false };
      // Keep the moves coming once the pointer leaves the title (a finger
      // gets this implicitly; a mouse or pen doesn't).
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // a pointer the browser already let go of
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      const s0 = swipe.current;
      if (!s0 || s0.done) return;
      const dx = e.clientX - s0.x;
      const dy = e.clientY - s0.y;
      if (Math.abs(dy) > FOLD_SWIPE_PX && Math.abs(dy) > Math.abs(dx) * 1.5) {
        s0.done = true;
        onFold();
      }
    },
    onPointerUp: () => {
      // A swipe's own click (it lands after pointerup) must not undo the fold.
      if (swipe.current?.done) window.setTimeout(() => (swipe.current = null), 0);
      else swipe.current = null;
    },
    onClickCapture: (e: React.MouseEvent) => {
      if (swipe.current?.done) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    onPointerCancel: () => {
      swipe.current = null;
    },
    onDoubleClick: (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-noswipe]")) return;
      onFold();
    },
  };
  const on = deviceOn(device);
  const hasPower = findParam(device, "Device On") !== undefined;
  const heard = HEARD_DEVICES.has(device.name);
  const placeholder = isPlaceholder(device);
  const stock = placeholder ? stockDevice(device.name) : null;
  const Editor = editorFor(device.name);
  if (folded) {
    return (
      <div
        className={cn("flex w-9 shrink-0 snap-start flex-col items-center gap-2 rounded-md border border-panelline bg-[var(--surface-panel)] py-2 touch-pan-x", !on && "opacity-60")}
        data-testid="device-box"
        data-device={device.name}
        data-folded
        {...swipeHandlers}
      >
        <button
          type="button"
          disabled={!editable || !hasPower}
          onClick={onToggle}
          data-noswipe
          aria-pressed={on}
          aria-label={`${device.name} ${on ? "on" : "off"}`}
          className={cn("size-3.5 shrink-0 rounded-full border", on ? "border-[var(--changed)] bg-[var(--changed)]" : "border-muted-foreground bg-transparent")}
        />
        <button type="button" onClick={onFold} className="flex min-h-0 flex-1 flex-col items-center gap-2" aria-label={`Unfold ${device.name}`} title={`Unfold ${device.name}`}>
          <span className="text-[10px] text-muted-foreground">▸</span>
          <span className="whitespace-nowrap text-[11px] font-semibold [writing-mode:vertical-rl] rotate-180">{device.name}</span>
        </button>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex shrink-0 snap-start flex-col rounded-md border border-panelline bg-[var(--surface-panel)]",
        // A device wider than a phone scrolls inside itself rather than past the edge.
        compact && "max-w-[calc(100vw-1.5rem)]",
        !on && "opacity-60",
      )}
      data-testid="device-box"
      data-device={device.name}
    >
      <div className={cn("flex items-center gap-1.5 border-b border-panelline px-1.5 touch-pan-x select-none", compact ? "h-9" : "h-6")} {...swipeHandlers}>
        <button
          type="button"
          onClick={onFold}
          data-noswipe
          className={cn("shrink-0 rounded text-muted-foreground hover:text-foreground", compact ? "px-1.5 text-xs" : "px-0.5 text-[10px]")}
          aria-label={`Fold ${device.name}`}
          title="Fold (or double-click the title)"
        >
          ▾
        </button>
        <button
          type="button"
          disabled={!editable || !hasPower}
          onClick={onToggle}
          data-noswipe
          aria-pressed={on}
          aria-label={`${device.name} ${on ? "on" : "off"}`}
          title={hasPower ? `Switch ${device.name} ${on ? "off" : "on"}` : device.name}
          className={cn("size-3 shrink-0 rounded-full border", on ? "border-[var(--changed)] bg-[var(--changed)]" : "border-muted-foreground bg-transparent")}
        />
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold" title={device.name}>
          {device.name}
        </span>
        <span
          className={cn("shrink-0 rounded px-1 text-[8px] uppercase tracking-wide", heard ? "bg-primary/15 text-primary" : "bg-[var(--control)] text-muted-foreground")}
          title={heard ? "Heard here through a browser stand-in, driven by the Set's values" : "Not modelled in the browser: it plays dry here, its knobs still move Live's"}
        >
          {heard ? "heard" : "dry"}
        </span>
        {onRemove && (
          <button type="button" onClick={onRemove} data-noswipe className="shrink-0 rounded px-1 text-[10px] text-muted-foreground hover:text-foreground" aria-label={`Remove ${device.name}`} title="Remove this device from the Set's chain">
            ✕
          </button>
        )}
      </div>
      <div className={cn("p-2", compact && "scrollbar-thin overflow-x-auto")}>
        {placeholder ? (
          stock ? (
            <div className="flex flex-col gap-1">
              <div className="text-[10px] text-[var(--changed)]">Live is adding this — heard at its defaults until the Set fills it in.</div>
              <Editor device={stock} bpm={bpm} disabled set={set} />
            </div>
          ) : (
            <div className="flex h-full min-w-[140px] items-center text-[10px] text-muted-foreground">Waiting for the Set to report its parameters…</div>
          )
        ) : (
          <Editor device={device} bpm={bpm} disabled={!editable} set={set} />
        )}
      </div>
    </div>
  );
}

/** Live's drop area at the end of the chain: opens the picker. */
function AddDevice({ onOpen, empty, compact = false }: { onOpen: () => void; empty: boolean; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-md border border-dashed border-panelline px-4 text-xs text-muted-foreground hover:border-foreground/30 hover:text-foreground",
        empty ? "min-w-[220px]" : compact ? "min-w-[96px]" : "min-w-[120px]",
      )}
    >
      <span className="text-lg leading-none">+</span>
      <span>{empty ? "No devices yet — add one" : "Add a device"}</span>
    </button>
  );
}

/** Live's built-ins, to add: a search, then large rows — the ones the
 * browser plays first, then the ones that only sync to Live. */
export function DevicePicker({ kind, onPick }: { kind: "audio" | "midi"; onPick: (name: string) => void }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const groups = deviceCatalog(kind)
    .map((g) => ({ group: g.group, names: g.names.filter((n) => !q || n.toLowerCase().includes(q)) }))
    .filter((g) => g.names.length > 0);
  const heard = groups.flatMap((g) => g.names.filter((n) => HEARD_DEVICES.has(n)));
  const rest = groups.map((g) => ({ group: g.group, names: g.names.filter((n) => !HEARD_DEVICES.has(n)) })).filter((g) => g.names.length > 0);
  const row = (n: string, playable: boolean) => (
    <button
      key={n}
      type="button"
      onClick={() => onPick(n)}
      className="flex min-h-11 items-center justify-between gap-2 rounded-lg border border-panelline bg-[var(--surface-panel)] px-3 text-left text-sm active:bg-white/10 hover:border-foreground/30"
    >
      <span className="truncate">{n}</span>
      {playable && <span className="shrink-0 rounded bg-primary/15 px-1.5 text-[9px] uppercase tracking-wide text-primary">heard</span>}
    </button>
  );
  return (
    <div className="flex max-h-[min(55dvh,420px)] min-h-[212px] flex-col gap-2 p-2" data-testid="device-picker">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter" && heard.length + rest.reduce((n, g) => n + g.names.length, 0) === 1) onPick(heard[0] ?? rest[0]!.names[0]!);
        }}
        placeholder="Search Live's devices"
        aria-label="Search devices"
        className="h-10 shrink-0 rounded-lg border border-[var(--control-line)] bg-[var(--control)] px-3 text-sm text-foreground sm:h-8"
      />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        {heard.length > 0 && (
          <section className="mb-3">
            <h3 className="mb-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Heard in the browser</h3>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">{heard.map((n) => row(n, true))}</div>
          </section>
        )}
        {rest.map((g) => (
          <section key={g.group} className="mb-3">
            <h3 className="mb-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{g.group} — sync to Live, dry here</h3>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">{g.names.map((n) => row(n, false))}</div>
          </section>
        ))}
        {heard.length === 0 && rest.length === 0 && <div className="p-2 text-xs text-muted-foreground">No device of Live&rsquo;s by that name.</div>}
      </div>
    </div>
  );
}
